import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Row, Col, Card, CardBody, Container, Spinner } from 'reactstrap';
import { useHistory } from 'react-router-dom';

import * as landingPagesActions from '../../store/landingPages/actions';
import * as countriesActions from '../../store/countries/actions';
import * as campaignsActions from '../../store/campaigns/actions';
import { useDispatch, useSelector } from 'react-redux';
import { actualCountry } from '../../selectors/country';

import Modal from '../../components/Common/Modal/Modal';
import ShowSingleElement from '../../components/Common/ShowSingleElement';
import Breadcrumbs from '../../components/Common/Breadcrumb';
import DataTableNext from '../../components/Common/DataTable/DataTableNext';
import createTableColumns from '../../components/LandingPages/tableColumns';
import {
  CreateButton,
  ExportButtonPlacement,
  ToggleColumnButton,
} from '../../components/Common/DataTable/tableActionButtons';
import { addOrRemove } from '../../helpers/sharedFunction';

const List = () => {
  const loadingPages = useSelector((state) => state.LandingPage.loading);
  const loading = loadingPages;

  const landingPages = useSelector((state) => state.LandingPage.landingPages);
  const countries = useSelector((state) => state.Country.countries);
  const campaigns = useSelector((state) => state.Campaign.campaigns);
  const country = useSelector(actualCountry);
  const countryId = country ? country.id : null;

  const [viewModal, setViewModal] = useState(false);
  const [selectedPage, setSelectedPage] = useState(null);

  const [filters, setFilters] = useState([]);
  const [sorting, setSorting] = useState([]);
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);

  const dispatch = useDispatch();
  const history = useHistory();

  useEffect(() => {
    dispatch(countriesActions.syncCountries());
  }, [dispatch]);

  useEffect(() => {
    if (countryId) {
      dispatch(landingPagesActions.syncLandingPages());
      dispatch(campaignsActions.syncCampaigns());
    }

    return () => {
      dispatch(landingPagesActions.resetState());
    };
  }, [dispatch, countryId]);

  const openEditModal = useCallback((page = null) => {
    if (page && page.id) {
      history.push(`/landing-pages/${page.id}/edit`);
    } else {
      history.push('/landing-pages/new');
    }
  }, [history]);

  const openViewModal = useCallback((page) => {
    setSelectedPage(page);
    setViewModal(true);
  }, []);

  const openArchiveModal = useCallback((page) => {
    dispatch(landingPagesActions.archiveLandingPage(page.id));
  }, [dispatch]);

  const columns = useMemo(
    () =>
      createTableColumns(
        openViewModal,
        openEditModal,
        openArchiveModal,
        countries,
        campaigns,
      ),
    [countries, campaigns, openArchiveModal, openEditModal, openViewModal],
  );
  const [hiddenColumns, setHiddenColumns] = useState(
    columns.filter(({ hidden }) => !!hidden).map(({ name }) => name),
  );
  const handleToggleColumn = (name) => {
    setHiddenColumns(addOrRemove(hiddenColumns, name));
  };

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid={true}>
          <Breadcrumbs title="Landing Pages" loading={loading} />
          <Row>
            <Col className="col-12">
              <Card>
                <CardBody>
                  <Row className="align-items-center justify-content-end mb-2">
                    <Spinner
                      color="secondary"
                      animation="border"
                      hidden={!loading}
                    />
                    <CreateButton
                      label="Create new Landing Page"
                      disabled={loading}
                      onPress={() => openEditModal()}
                    />
                    <ToggleColumnButton
                      columns={columns}
                      hiddenColumns={hiddenColumns}
                      onToggleColumn={handleToggleColumn}
                    />
                    <ExportButtonPlacement />
                  </Row>
                  <DataTableNext
                    loading={loading}
                    rows={landingPages}
                    columns={columns}
                    filters={filters}
                    onFiltersChange={setFilters}
                    sorting={sorting}
                    onSortingChange={setSorting}
                    currentPage={currentPage}
                    onCurrentPageChange={setCurrentPage}
                    hiddenColumns={hiddenColumns}
                    onHiddenColumnsChange={setHiddenColumns}
                    pageSize={pageSize}
                    onPageSizeChange={setPageSize}
                  />
                </CardBody>
              </Card>
            </Col>
          </Row>
          <Modal
            size="lg"
            title="Landing Page"
            isOpen={viewModal}
            toggle={() => setViewModal(!viewModal)}
            showClose
            scrollable={false}>
            <ShowSingleElement
              element={selectedPage}
              collection="landingPages"
              icon="bx bx-globe"
            />
          </Modal>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default List;
